import React from 'react';
import type { UserDetailsResponse } from '../../schemas/validation';
import type { UserDetailsState } from './useUserDetailsState';

interface AdminOverridesCardProps {
  subscription: UserDetailsResponse['data']['subscription'];
  state: UserDetailsState;
  onOpenForm: () => void;
  onSave: () => void;
  onClear: () => void;
}

/** Format plan price for display. API may return a number or a NUMERIC string. */
function formatPlanPrice(price: number | string | null | undefined): string {
  if (price == null || price === '') return '0.00';
  const amount = typeof price === 'number' ? price : Number(price);
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
}

export const AdminOverridesCard: React.FC<AdminOverridesCardProps> = ({
  subscription,
  state,
  onOpenForm,
  onSave,
  onClear,
}) => {
  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Admin Overrides</h3>
          <p className="text-sm text-gray-500 mt-1">
            Override plan limits and pricing for this user
          </p>
        </div>
        {!state.showOverrideForm && (
          <button onClick={onOpenForm} className="btn btn-primary text-sm">
            Edit Overrides
          </button>
        )}
      </div>
      <div className="px-6 py-4">
        {state.showOverrideForm ? (
          <div className="space-y-4">
            {state.overrideError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{state.overrideError}</p>
              </div>
            )}
            {state.overrideSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-600">{state.overrideSuccess}</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="overrideMaxSites"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Max Sites
                </label>
                <input
                  type="number"
                  id="overrideMaxSites"
                  value={state.overrideMaxSites}
                  onChange={e => state.setOverrideMaxSites(e.target.value)}
                  placeholder="Leave empty to use plan default"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Plan default: {subscription?.limits?.maxSites ?? 1}
                </p>
              </div>
              <div>
                <label
                  htmlFor="overrideMaxPagesPerSite"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Max Pages per Site
                </label>
                <input
                  type="number"
                  id="overrideMaxPagesPerSite"
                  value={state.overrideMaxPagesPerSite}
                  onChange={e => state.setOverrideMaxPagesPerSite(e.target.value)}
                  placeholder="Leave empty to use plan default"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Plan default:{' '}
                  {subscription?.limits?.maxPagesPerSite === -1
                    ? 'Unlimited'
                    : (subscription?.limits?.maxPagesPerSite ?? 5)}
                </p>
              </div>
            </div>

            {state.hasActiveSubscription ? (
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Price Override</h4>
                <div className="max-w-xs">
                  <label
                    htmlFor="overridePrice"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    New {state.billingPeriod === 'yearly' ? 'Yearly' : 'Monthly'} Price ($)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      id="overridePrice"
                      value={state.overridePrice}
                      onChange={e => state.setOverridePrice(e.target.value)}
                      placeholder="Enter new price"
                      min="0"
                      step="0.01"
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Current price ({state.planName || 'Plan'}): $
                    {formatPlanPrice(state.currentPlanPrice)}/
                    {state.billingPeriod === 'yearly' ? 'year' : 'month'}
                    {state.hasCustomPrice && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        Custom
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    This will update the user's Stripe subscription. New price takes effect on next
                    billing cycle.
                  </p>
                </div>
              </div>
            ) : (
              <div className="pt-4 border-t border-gray-200">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Price Override:</span> Not available for free plan
                    users. Price overrides can only be applied to users with an active paid
                    subscription.
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <button onClick={onClear} className="text-sm text-gray-600 hover:text-gray-800">
                Clear All Overrides
              </button>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => state.setShowOverrideForm(false)}
                  className="btn btn-ghost"
                  disabled={state.savingOverrides}
                >
                  Cancel
                </button>
                <button
                  onClick={onSave}
                  className="btn btn-primary"
                  disabled={state.savingOverrides}
                >
                  {state.savingOverrides ? 'Saving...' : 'Save Overrides'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <dt className="text-sm font-medium text-gray-500">Max Sites Override</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {subscription?.limits?.maxSites !== undefined ? (
                  <span className="font-medium">{subscription.limits.maxSites}</span>
                ) : (
                  <span className="text-gray-400">Using plan default</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Max Pages per Site Override</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {subscription?.limits?.maxPagesPerSite !== undefined ? (
                  <span className="font-medium">
                    {subscription.limits.maxPagesPerSite === -1
                      ? 'Unlimited'
                      : subscription.limits.maxPagesPerSite}
                  </span>
                ) : (
                  <span className="text-gray-400">Using plan default</span>
                )}
              </dd>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
